import React from 'react';
import PropTypes from 'prop-types';
import Text from '@firsttable/web-components/atoms/Text';
import parse, { attributesToProps, domToReact } from 'html-react-parser';
import { BodyTitle, Title, Divider, Link } from '@firsttable/web-components/atoms';
import { awsImage } from '@firsttable/functions';
import styled, { css } from 'styled-components';
import media from 'styled-media-query';
import { breakpointsObject as bp } from '@firsttable/web-theme';
import { switchProp } from 'styled-tools';
import dynamic from 'next/dynamic';
import Box from '@firsttable/web-components/atoms/Box/Box';
import Button from '@components/analytics/components/Button';
import ConsentGatedIframe from '@components/molecules/ConsentGatedIframe';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import NavLink from '../components/GatsbyLinkConvertor';
import CloudfrontImage from '../components/CloudfrontImage';
import { WEBSITE_URL } from './env';

const LoyaltyEarnDefinitions = dynamic(
  () => import('../components/organisms/Loyalty/LoyaltyEarnDefinitions')
);
const LoyaltyLinks = dynamic(() => import('../components/organisms/Loyalty/LoyaltyLinks'));
const LoyaltyTierDetailsAccordion = dynamic(
  () => import('../components/organisms/Loyalty/LoyaltyTierDetailsAccordion')
);
const LoyaltyTierBenefitMatrix = dynamic(
  () => import('../components/organisms/Loyalty/LoyaltyTierBenefitMatrix')
);
const LoyaltyTierPeriods = dynamic(
  () => import('../components/organisms/Loyalty/LoyaltyTierPeriods')
);
const CompetitionLeaderboard = dynamic(
  () => import('../components/organisms/Competition/CompetitionLeaderboard')
);

const ImageWrapper = styled.span`
  display: block;
  border-radius: ${({ rounded }) => (rounded ? '9999px' : '2px')};
  overflow: hidden;
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  height: 'auto';
  max-width: 100%;
  * {
    display: block !important;
    width: 100% !important;
  }
  ${switchProp('size', {
    large: css`
      margin-left: -16px;
      margin-right: -16px;
      ${media.greaterThan(bp.m)`
      margin: 50px -50px;
    `}
    `,
    portrait: css`
      min-height: 400px;
      max-height: 50vh;
      img {
        object-fit: unset;
        min-height: 400px !important;
        max-height: 50vh !important;
        width: auto !important;
      }

      @supports (height: 100dvh) {
        max-height: 50dvh;
        img {
          max-height: 50dvh !important;
        }
      }
    `,
    floatLeft: css`
      float: left;
      margin: 0 20px 20px 0;
    `,
    floatRight: css`
      float: right;
      margin: 0 20px 20px 0;
    `,
    floatCenter: css`
      float: left;
      position: relative;
      left: 50%;
      transform: translateX(-50%);
    `,
  })}
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  table {
    width: 100%;
    table-layout: fixed;
    overflow-wrap: break-word;
  }
`;

const Blockquote = styled.blockquote`
  border-left: 4px solid;
  border-color: ${({ theme }) => theme.colors.brand.blue};
  padding: 10px 0 10px 20px;
  margin: 20px 0;
  font-style: italic;
  p:last-child {
    margin-bottom: 0;
  }
`;

const ImageExtractor = ({ src, alt, width, height, className }) => {
  if (src && src.search(/public/) !== -1) {
    if (!width || !height) {
      return <img src={src} alt={alt} className={className} />;
    }

    // for public path images
    const key = src.match(new RegExp('/public/([^]+)'));

    // File type
    const fileType = src.split('.').pop();

    if (key && fileType !== 'svg') {
      const awsURI = `/public/${key[1]}`;

      let size = 'large';
      if (width && parseInt(width) < 400) {
        size = 'small';
      } else if (width && height && parseInt(height) > parseInt(width)) {
        // size = 'portrait';
      }

      const floatLeft = className?.includes('left') || className?.includes('leftAlone');
      if (floatLeft) {
        size = 'floatLeft';
      }

      const floatRight = className?.includes('right') || className?.includes('rightAlone');
      if (floatRight) {
        size = 'floatRight';
      }

      const center = className?.includes('center');
      if (center) {
        size = 'floatCenter';
      }

      const clear =
        className?.includes('leftAlone') ||
        className?.includes('rightAlone') ||
        className?.includes('center') ? (
          <span style={{ display: 'block', clear: 'both' }} />
        ) : null;

      // if className contains 'unoptimized'
      const unoptimized = className?.includes('unoptimized') || fileType === 'png';

      const rounded = className?.includes('rounded-full');

      return (
        <>
          <ImageWrapper
            className="bg-pulse-animation"
            rounded={rounded}
            size={size}
            width={width}
            height={height}>
            <CloudfrontImage
              src={awsImage(awsURI, width, height, unoptimized ? '' : null)}
              alt={alt}
              className={`img-fluid ${className || ''}`}
              width={width}
              height={height}
              style={{ width: '100%', height: '100%' }}
              sizes="(max-width: 768px) 100vw, 680px"
              unoptimized={unoptimized}
            />
          </ImageWrapper>
          {clear}
        </>
      );
    }
  }
  return <img src={src} alt={alt} width={width} height={height} className={className} />;
};

ImageExtractor.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  className: PropTypes.string,
};

const options = {
  replace: (node) => {
    const { children, name: tagName, attribs, prev, next } = node;
    if (node.type === 'comment') {
      return;
    }

    if (node.type === 'text') {
      if (node.data.trim() === '') {
        return;
      }
      return node.data;
    }

    const attributes = {};

    if (attribs.class?.includes('text-center')) {
      attributes.textAlign = 'center';
    } else if (attribs.class?.includes('text-right')) {
      attributes.textAlign = 'right';
    } else if (attribs.class?.includes('text-justify')) {
      attributes.textAlign = 'justify';
    } else if (attribs.class?.includes('text-left')) {
      attributes.textAlign = 'left';
    }

    switch (tagName) {
      case 'span':
        if (!children || children.length === 0) {
          return <></>;
        }
        return null;
      case 'p':
        if (!children || children.length === 0) {
          return <></>;
        }
        return <Text {...attributes}>{domToReact(children, options)}</Text>;
      case 'h1':
        return (
          <Title as={tagName} {...attributes}>
            {domToReact(children, options)}
          </Title>
        );
      case 'h2':
        return (
          <BodyTitle as={tagName} size="l" {...attributes}>
            {domToReact(children, options)}
          </BodyTitle>
        );
      case 'h3':
        return (
          <BodyTitle as={tagName} size="m" {...attributes}>
            {domToReact(children, options)}
          </BodyTitle>
        );
      case 'h4':
      case 'h5':
      case 'h6':
        return (
          <BodyTitle as={tagName} size="s" {...attributes}>
            {domToReact(children, options)}
          </BodyTitle>
        );
      case 'a': {
        let { href } = attribs;
        href = href?.replace(WEBSITE_URL, '/');

        if (attribs.class && attribs.class.match('btn')) {
          return (
            <span className="parsed-html-btn">
              <TrackProvider button_name="parsed html button">
                <Button href={href} as="a" NavComponent={NavLink} mb={5} noLinkTracking>
                  {domToReact(children, options)}
                </Button>
              </TrackProvider>
            </span>
          );
        }
        if (
          href &&
          (href.startsWith('http') || href.startsWith('mailto') || attribs.target === '_blank')
        ) {
          return (
            <Link href={href} color="brand.red" target="__blank" rel="noopener">
              {domToReact(children, options)}
            </Link>
          );
        }
        return (
          <Link NavComponent={NavLink} to={href} color="brand.red">
            {domToReact(children, options)}
          </Link>
        );
      }
      case 'hr':
        return <Divider mt="20px" mb="20px" />;
      case 'img': {
        if (!attribs.src) {
          return null;
        }
        return <ImageExtractor {...attributesToProps(attribs)} />;
      }
      case 'div': {
        if (attribs['data-component'] !== undefined) {
          const my = 40;
          switch (attribs['data-component']) {
            case 'LoyaltyEarnDefinitions':
              return (
                <Box my={my}>
                  <LoyaltyEarnDefinitions renderHtml={parseHtml} />
                </Box>
              );
            case 'LoyaltyLinks':
              return (
                <Box my={my}>
                  <LoyaltyLinks />
                </Box>
              );
            case 'LoyaltyTierBenefitMatrix':
              return (
                <Box my={my}>
                  <LoyaltyTierBenefitMatrix />
                </Box>
              );
            case 'LoyaltyTierDetailsAccordion':
              return (
                <Box my={my}>
                  <LoyaltyTierDetailsAccordion renderHtml={parseHtml} />
                </Box>
              );
            case 'LoyaltyTierPeriods':
              return (
                <Box my={my}>
                  <LoyaltyTierPeriods />
                </Box>
              );
            case 'CompetitionLeaderboard':
              return (
                <Box my={20}>
                  <CompetitionLeaderboard
                    competitionId={attribs['data-competition-id']}
                    limit={parseInt(attribs['data-limit'])}
                  />
                </Box>
              );
            default:
              return null;
          }
        }

        return node;
      }
      case 'table': {
        return (
          <TableWrapper>
            <table {...attributesToProps(attribs)}>{domToReact(children, options)}</table>
          </TableWrapper>
        );
      }
      case 'iframe': {
        return (
          <ConsentGatedIframe attribs={attribs}>{domToReact(children, options)}</ConsentGatedIframe>
        );
      }
      case 'blockquote': {
        return <Blockquote>{domToReact(children, options)}</Blockquote>;
      }
      case 'script':
      case 'style':
      case 'noscript':
      case 'link':
      case 'meta':
      case 'head':
      case 'title':
      case 'html':
      case 'body': {
        return null;
      }
      default: {
        // remove empty html tags
        if (
          node.type === 'text' &&
          node.data.length === 1 &&
          node.data !== '"' &&
          node.data !== '.'
        ) {
          return <></>;
        }
        // Default, re-create the node
        return React.createElement(
          tagName,
          attributesToProps(attribs),
          children.length ? domToReact(children, options) : null // If no children, pass in null (for self-closing tags like <link />)
        );
      }
    }
  },
};

const parseHtml = (content) =>
  content && // Skip null content
  parse(content, options);

export default parseHtml;
